import React, { useState, useEffect } from "react";
import { generatePath, Link, useHistory } from "react-router-dom";
import { useQueryClient } from "react-query";
import dayjs from "dayjs";
import { Box, Select, MenuItem, FormControl } from "@mui/material";
import KrgToggleButton from "@kargo/shared-components.krg-toggle-button";
import {
  VARIANT_ENUM as BUTTON_VARIANT_ENUM,
  SIZE_ENUM as BUTTON_SIZE_ENUM,
} from "@kargo/shared-components.krg-button";
import CreatePropertyModal from "../../../components/Modals/CreatePropertyModal";
import useSnackbar from "../../../hooks/useSnackbar";
import LoadingCircle from "../../../components/LoadingCircle";
import Table from "../../../components/Table";
import TrackedKrgButton from "../../../components/TrackedButton/TrackedKrgButton";
import { useSuperAdminContext } from "../../../routes/super_admin";

import "./styles.scss";
import { PATHS } from "../../../constants/paths";
import { Domain } from "../../../api/types";
import useAPI from "../../../api/useAPI";
import { usePublisherContext } from "../../../hooks/usePublisherProvider";

type IDomainPage = {
  asAdmin: boolean;
};

export default function DomainsPage(props: IDomainPage) {
  const { addPathToBreadCrumb, domains, domainsQuery } = useSuperAdminContext();
  const { asAdmin = false } = props;
  const { publisherSlug } = usePublisherContext(asAdmin);
  const { snackbarSuccess, snackbarError } = useSnackbar();
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [userDomains, setUserDomains] = useState<Domain[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const history = useHistory();
  const queryClient = useQueryClient();
  const { api } = useAPI();

  // Add breadcrumb
  useEffect(() => {
    if (!asAdmin) {
      history.push(PATHS.PUBLISHER_HUB);
    }
    addPathToBreadCrumb([
      {
        path: generatePath(PATHS.ADMIN_DOMAIN, { slug: publisherSlug }),
        pathName: "Domains",
      },
    ]);
  }, []);
  useEffect(() => {
    if (domains) {
      const tempDomains = domains.map((domain) => ({
        ...domain,
        id: domain.domain,
      }));
      setUserDomains(tempDomains);
    }
  }, [domains]);

  if (domainsQuery.isFetching) {
    return (
      <div className="krg-loading-wrapper">
        <LoadingCircle />
      </div>
    );
  }

  const handleRecommenderToggle = (domainToken: string, enabled: boolean) => {
    const index = userDomains.findIndex(
      (domain) => domain.token === domainToken,
    );
    if (index === -1) return;

    const target = userDomains[index];
    if (target.recommender_enabled === enabled) return;

    const updatedDomains = [...userDomains];
    updatedDomains[index] = {
      ...target,
      recommender_enabled: enabled,
      ...(!target.recommender_source && { recommender_source: "1PC" }),
    };

    setUserDomains(updatedDomains);
    setHasUnsavedChanges(true);
  };

  const handleSourceChange = (domainToken: string, source: "VE" | "1PC") => {
    const index = userDomains.findIndex(
      (domain) => domain.token === domainToken,
    );
    if (index === -1) return;

    const target = userDomains[index];
    if (target.recommender_source === source) return;

    const updatedDomains = [...userDomains];
    updatedDomains[index] = {
      ...target,
      recommender_source: source,
    };

    setUserDomains(updatedDomains);
    setHasUnsavedChanges(true);
  };

  const handleSaveChanges = async () => {
    const domainMap = new Map(domains.map((d) => [d.token, d]));
    try {
      const updatedDomains = userDomains
        .filter((userDomain) => {
          const originalDomain = domainMap.get(userDomain.token);
          return (
            originalDomain?.recommender_enabled !==
              userDomain.recommender_enabled ||
            originalDomain?.recommender_source !== userDomain.recommender_source
          );
        })
        .map((domain) => ({
          token: domain.token,
          recommender_enabled: domain.recommender_enabled,
          recommender_source: domain.recommender_source,
        }));

      if (updatedDomains.length === 0) {
        snackbarSuccess("No changes to save");
        setHasUnsavedChanges(false);
        return;
      }
      await api.bulkUpdateDomains({ domains: updatedDomains });
      queryClient.invalidateQueries({ queryKey: ["domain-configs"] });
      snackbarSuccess(`Domain(s) updated successfully`);
      setHasUnsavedChanges(false);
    } catch (error) {
      snackbarError("Error updating domain(s)");
      console.error(error);
    }
  };

  const columns = [
    {
      field: "domain",
      headerName: "Domains",
      renderCell: (row) => (
        <Link
          to={generatePath(PATHS.ADMIN_DOMAIN_CONFIG, {
            slug: publisherSlug,
            domainToken: row.token,
          })}
          className="krg-ellipses-text"
        >
          {row.domain}
        </Link>
      ),
    },
    {
      field: "updated_at",
      headerName: "Last update",
      renderCell: (row) => (
        <p>
          {dayjs(row.updated_at).format("DD/MMM/YY ")}
          {dayjs(row.updated_at).format(" HH:mm:ss")}
        </p>
      ),
    },
    {
      field: "recommender_enabled",
      headerName: "Recommender Enabled",
      renderCell: (row) => {
        const domain = userDomains.find((d) => d.token === row.token);
        return (
          <KrgToggleButton
            buttons={[
              { label: "Yes", value: "true" },
              { label: "No", value: "false" },
            ]}
            size={KrgToggleButton.SIZE_ENUM.small}
            color={
              domain?.recommender_enabled
                ? KrgToggleButton.COLOR_ENUM.success
                : KrgToggleButton.COLOR_ENUM.error
            }
            onChange={(value) => {
              if (asAdmin) {
                handleRecommenderToggle(row.token, value[0] === "true");
              }
            }}
            isSingleSelection
            selectedButtons={[domain?.recommender_enabled ? "true" : "false"]}
            disabled={!asAdmin}
          />
        );
      },
    },
    {
      field: "recommender_source",
      headerName: "Recommender Source",
      renderCell: (row) => {
        const domain = userDomains.find((d) => d.token === row.token);
        return (
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={domain?.recommender_source || "1PC"}
              onChange={(e) =>
                handleSourceChange(row.token, e.target.value as "VE" | "1PC")
              }
              disabled={!asAdmin || !domain?.recommender_enabled}
              sx={{ height: "32px" }}
            >
              <MenuItem value="1PC">1PC</MenuItem>
              <MenuItem value="VE">VE</MenuItem>
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: "",
      headerName: "No. of Players Configs",
      onClick: (__, row) => {
        addPathToBreadCrumb([
          {
            path: generatePath(PATHS.ADMIN_DOMAIN_CONFIG, {
              slug: publisherSlug,
              domainToken: row.token,
            }),
            pathName: row.domain,
          },
        ]);
        history.push(
          generatePath(PATHS.ADMIN_DOMAIN_CONFIG, {
            slug: publisherSlug,
            domainToken: row.token,
          }),
          {
            config: row.configs,
          },
        );
      },
      renderCell: (row) => (
        <p style={{ textAlign: "end", width: "122px" }}>{row.configs.length}</p>
      ),
    },
  ];

  return (
    <Box padding="20px">
      <Box padding="0 30px 0 0" display="flex" justifyContent="end" gap={2}>
        {asAdmin && hasUnsavedChanges && (
          <TrackedKrgButton
            id="save-domain-settings"
            text="Save Changes"
            onClick={handleSaveChanges}
            variant={BUTTON_VARIANT_ENUM.contained}
            size={BUTTON_SIZE_ENUM.small}
            color="primary"
          />
        )}
        <TrackedKrgButton
          id="add-domain"
          text="Add domain"
          onClick={() => setModalIsOpen(true)}
          variant={BUTTON_VARIANT_ENUM.contained}
          size={BUTTON_SIZE_ENUM.small}
        />
      </Box>
      <CreatePropertyModal
        asAdmin={asAdmin}
        title="Add New Domain"
        label="Domain"
        isOpen={modalIsOpen}
        showCheckbox
        onClose={() => setModalIsOpen(false)}
        onSubmit={() => setModalIsOpen(false)}
        createOptions={{
          onSuccess: () => {
            snackbarSuccess("Created New Domain");
          },
          onError: () => {
            snackbarError(
              "Error creating domain, check the console for more information.",
            );
          },
        }}
      />
      <Box display="flex" flexDirection="column" justifyContent="center">
        <Table columns={columns} rows={userDomains} size={12} />
      </Box>
    </Box>
  );
}
